/**
 * NLC Platform V2 — Pricing pure functions.
 *
 * All prices are in CENTS (integer). Call Math.round on every dollar × sqft
 * multiplication — never accumulate floating-point rounding errors.
 *
 * Rates come from admin_settings (loaded via loadRatesFromSettings()).
 * NEVER pass hardcoded rate values. NEVER import from DB or env here —
 * callers pass rates in, making this fully testable without infrastructure.
 *
 * ---------------------------------------------------------------------------
 * BANDED PRICING (locked 2026-09-04, replaces the flat per-sqft card)
 * ---------------------------------------------------------------------------
 * Furnished services price on THREE MARGINAL BANDS, like tax brackets — each
 * band's rate applies only to the square footage that falls inside it:
 *
 *     band 1:              0 – 1,500 sqft
 *     band 2:          1,501 – 3,000 sqft
 *     band 3:          3,001 – 6,000 sqft (and beyond, flagged for site visit)
 *
 * Why: a flat $/sqft rate assumes cleaning time scales linearly with area. It
 * doesn't — kitchens and bathrooms dominate the clock and don't grow with
 * square footage. Market data (2026) shows effective $/sqft falling ~25% from
 * small homes to large, then flattening past 3,000 sqft. A flat card is
 * therefore underpriced at the small end and overpriced at the large end,
 * which is what forced manual discounting on every large job.
 *
 * UNFURNISHED is deliberately NOT banded — an empty home has no scale economy
 * (measured 125–250 sqft per labor-hour at every size), so it stays flat.
 *
 * Minimums exist because a truck roll costs the same regardless of home size:
 *   jobMinimum       — floor for every job
 *   deepCleanMinimum — higher floor for deep cleans, which are labor-dense.
 *                      Without it a small deep clean pays the crew below
 *                      Florida minimum wage ($15.00 from 2026-09-30).
 *
 * ⚠️ jobMinimum was configurable in the settings UI from the beginning and
 * enforced NOWHERE until 2026-09-02 — `pricing_job_minimum` was written,
 * saved, and read by no code at all, so every under-minimum quote went out
 * at its raw per-sqft price. Set to 0 to genuinely have no minimum.
 *
 * The floor applies to the TOTAL (base + upgrade), not to `basePrice` before
 * the upgrade is added — flooring the base and then adding the upgrade on
 * top would push a job past the minimum twice and overcharge exactly the
 * smallest customers the minimum exists to make viable.
 *
 * estimatedHours = totalPrice_dollars / pricing_internal_hourly_rate
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Marginal rate bands for one service, in $/sqft. */
export interface PricingBands {
  band1: number; // $/sqft applied to sqft 0 – band1MaxSqft
  band2: number; // $/sqft applied to sqft band1MaxSqft – band2MaxSqft
  band3: number; // $/sqft applied to sqft above band2MaxSqft
}

export interface PricingRates {
  unfurnishedPerSqft: number;          // $/sqft — FLAT, any service type, no bands
  furnishedMaintenance: PricingBands;  // one-time maintenance visit
  furnishedDeep: PricingBands;         // deep clean
  recurringWeekly: PricingBands;
  recurringBiweekly: PricingBands;
  recurring4Weeks: PricingBands;
  deepUpgradePerSqft: number;          // add-on $/sqft, band 1 sqft only
  internalHourlyRate: number;          // $/hr
  jobMinimum: number;                  // $ floor for any job
  deepCleanMinimum: number;            // $ floor for deep cleans
  band1MaxSqft: number;                // upper edge of band 1
  band2MaxSqft: number;                // upper edge of band 2
  cardMaxSqft: number;                 // above this the card is advisory only
}

export interface PricingInput {
  sqft: number;                         // min 200, max 15000
  furnishingState: 'furnished' | 'unfurnished';
  serviceType: 'recurring_maintenance' | 'deep_clean' | 'one_time';
  recurrenceFrequency?: 'weekly' | 'biweekly' | 'every_4_weeks';
  includeDeepUpgrade?: boolean;
}

export interface PricingResult {
  basePrice: number;      // cents (integer) — banded/flat price, pre-minimum
  upgradeAmount: number;  // cents (integer) — 0 when no upgrade
  /** cents (integer) = basePrice + upgradeAmount + minimumAdjustment */
  totalPrice: number;
  estimatedHours: number; // decimal hours
  rateKey: string;        // which admin_settings key drove band 1
  rateUsed: number;       // $/sqft — the BAND 1 (headline) rate
  effectiveRate: number;  // $/sqft — totalPrice ÷ sqft, what the client actually pays
  requiresSiteVisit: boolean; // true above cardMaxSqft — quote is advisory
  /**
   * True when base + upgrade came out under the job minimum and the total
   * was raised to meet it. Callers that itemise a quote MUST surface this —
   * without it the line items add up to less than the total and the
   * client's first question is why.
   */
  minimumApplied: boolean;
  /**
   * cents (integer) — what the minimum added. 0 unless `minimumApplied`.
   * Exists so `basePrice + upgradeAmount + minimumAdjustment === totalPrice`
   * always holds, and a quote's arithmetic can be shown without a special case.
   */
  minimumAdjustment: number;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Apply marginal bands to a square footage.
 * Returns DOLLARS (not cents) — the caller rounds once, at the cents boundary.
 *
 * Marginal (not tiered-flat) on purpose: a flat rate keyed to a size bracket
 * creates a price cliff at every boundary, where one extra square foot can
 * drop the price by $50. Marginal bands are continuous and monotonic.
 */
function applyBands(bands: PricingBands, sqft: number, band1Max: number, band2Max: number): number {
  const inBand1 = Math.min(sqft, band1Max);
  const inBand2 = Math.max(0, Math.min(sqft, band2Max) - band1Max);
  const inBand3 = Math.max(0, sqft - band2Max);
  return inBand1 * bands.band1 + inBand2 * bands.band2 + inBand3 * bands.band3;
}

/** Pick the band set + settings key for a furnished job. */
function selectBands(
  rates: PricingRates,
  serviceType: PricingInput['serviceType'],
  recurrenceFrequency: PricingInput['recurrenceFrequency'],
): { bands: PricingBands; rateKey: string } {
  if (serviceType === 'deep_clean') {
    return { bands: rates.furnishedDeep, rateKey: 'pricing_furnished_deep_per_sqft' };
  }
  if (serviceType === 'one_time') {
    return { bands: rates.furnishedMaintenance, rateKey: 'pricing_furnished_maintenance_per_sqft' };
  }
  switch (recurrenceFrequency) {
    case 'weekly':
      return { bands: rates.recurringWeekly, rateKey: 'pricing_furnished_recurring_weekly_per_sqft' };
    case 'every_4_weeks':
      return { bands: rates.recurring4Weeks, rateKey: 'pricing_furnished_recurring_4wks_per_sqft' };
    case 'biweekly':
    default:
      // Fallback to biweekly if frequency missing for recurring
      return { bands: rates.recurringBiweekly, rateKey: 'pricing_furnished_recurring_biweekly_per_sqft' };
  }
}

/**
 * Raise a subtotal to the job minimum and report what that cost.
 *
 * Applied to the TOTAL (base + upgrade), never to `basePrice` alone — see the
 * file-header note on why flooring twice overcharges the smallest customers.
 */
function applyMinimum(
  subtotalCents: number,
  minimumDollars: number,
): { totalPrice: number; minimumApplied: boolean; minimumAdjustment: number } {
  const floorCents = Math.round((minimumDollars || 0) * 100);
  if (floorCents <= 0 || subtotalCents >= floorCents) {
    return { totalPrice: subtotalCents, minimumApplied: false, minimumAdjustment: 0 };
  }
  return {
    totalPrice: floorCents,
    minimumApplied: true,
    minimumAdjustment: floorCents - subtotalCents,
  };
}

// ---------------------------------------------------------------------------
// Public functions
// ---------------------------------------------------------------------------

/**
 * Calculate price for a job given rates and inputs.
 * All money values in the result are cents (integer).
 */
export function calculatePrice(rates: PricingRates, input: PricingInput): PricingResult {
  const { sqft, furnishingState, serviceType, recurrenceFrequency, includeDeepUpgrade } = input;

  const requiresSiteVisit = sqft > rates.cardMaxSqft;
  const minimumDollars = serviceType === 'deep_clean' ? rates.deepCleanMinimum : rates.jobMinimum;

  // Unfurnished — flat rate regardless of service type, no bands, no upgrade.
  if (furnishingState === 'unfurnished') {
    const rateUsed = rates.unfurnishedPerSqft;
    const basePrice = Math.round(sqft * rateUsed * 100);
    const floored = applyMinimum(basePrice, minimumDollars);
    const estimatedHours = floored.totalPrice / 100 / rates.internalHourlyRate;

    return {
      basePrice,
      upgradeAmount: 0,
      estimatedHours,
      rateKey: 'pricing_unfurnished_per_sqft',
      rateUsed,
      effectiveRate: sqft > 0 ? floored.totalPrice / 100 / sqft : 0,
      requiresSiteVisit,
      ...floored,
    };
  }

  const { bands, rateKey } = selectBands(rates, serviceType, recurrenceFrequency);
  const basePrice = Math.round(applyBands(bands, sqft, rates.band1MaxSqft, rates.band2MaxSqft) * 100);

  // Deep upgrade only applies to recurring visits, and only to band-1 sqft —
  // it is a first-visit catch-up on the areas a maintenance clean skips, which
  // are the same fixed areas that make band 1 expensive in the first place.
  const canUpgrade = serviceType === 'recurring_maintenance';
  const upgradeAmount =
    includeDeepUpgrade && canUpgrade
      ? Math.round(Math.min(sqft, rates.band1MaxSqft) * rates.deepUpgradePerSqft * 100)
      : 0;

  const floored = applyMinimum(basePrice + upgradeAmount, minimumDollars);
  const estimatedHours = floored.totalPrice / 100 / rates.internalHourlyRate;

  return {
    basePrice,
    upgradeAmount,
    estimatedHours,
    rateKey,
    rateUsed: bands.band1,
    effectiveRate: sqft > 0 ? floored.totalPrice / 100 / sqft : 0,
    requiresSiteVisit,
    ...floored,
  };
}

/**
 * Estimate job hours from a pricing input.
 * Convenience wrapper around calculatePrice().
 */
export function estimateHours(rates: PricingRates, input: PricingInput): number {
  return calculatePrice(rates, input).estimatedHours;
}

/**
 * Convert the raw admin_settings Record<string, string> into typed PricingRates.
 *
 * Band 1 reuses the ORIGINAL flat-rate settings keys — their meaning is now
 * "rate for the first band1MaxSqft square feet", so the existing settings UI
 * and any tenant that has customized them keep working. Band 2 and band 3 use
 * new *_band2_* / *_band3_* keys and fall back to band 1 when unseeded, which
 * degrades safely to the old flat behavior instead of to zero.
 */
/**
 * Priced add-ons. The NAMES live in src/app/data/scope.ts (the scope truth
 * doc); the DOLLARS live here, with the rest of the money. Keep them in sync —
 * scripts/verify-scope.ts fails the build if a key exists in one and not the
 * other.
 *
 * Values are the card Vikelas approved 2026-09-10. `windows` is a range
 * because it depends on pane count; the rest are flat.
 */
export const ADDON_PRICES: Record<string, { amount: number; max?: number; note?: string }> = {
  oven:    { amount: 35 },
  fridge:  { amount: 45 },
  windows: { amount: 50, max: 80, note: 'depends on pane count' },
  laundry: { amount: 25, note: 'per load, washed and folded' },
};

/** "$35" or "$50–$80" — one formatter so every surface prints add-ons alike. */
export function formatAddon(key: string): string {
  const a = ADDON_PRICES[key];
  if (!a) return '';
  return a.max ? `$${a.amount}\u2013$${a.max}` : `$${a.amount}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * THE RATE CARD — the one place any price on this site is changed.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Change a number here and it reaches EVERYTHING: the "from $X/sqft" figures in
 * page copy, the hero calculator, /pricing, /quote, /book-today, the widget on
 * /residential, /get-a-quote, and the /api/pricing/calculate endpoint. There is
 * no second place to edit and no cache to clear — they all read this object.
 *
 * Two guards keep that true, both run by `npm run build`:
 *   scripts/verify-rate-card.ts          — these values match the approved card,
 *                                          the budgeted hours, and the wage tiers
 *   scripts/verify-calculator-parity.ts  — every calculator returns the same
 *                                          number for the same inputs
 *
 * ── Why this is a plain object now (2026-09-10) ────────────────────────────
 * It used to live as the FALLBACK ARGUMENTS inside `loadRatesFromSettings()` —
 * `n('pricing_job_minimum', 185.00)` and so on. That function is called in four
 * places and every one passes `{}`, so all fourteen lookups missed and returned
 * their fallback. The real rate card was therefore the second argument to a
 * lookup helper inside a function named "load from settings", which reads as
 * though the numbers come from a database. They do not. Same values, same
 * behaviour, now visible.
 *
 * ── Band 1 is the approved card exactly ────────────────────────────────────
 * Verified at the 1,500 sqft reference job (1,500 == band1MaxSqft, so band 1 is
 * the whole price):
 *   weekly    1500 × 0.11 = $165 → floored to the $185 job minimum
 *   biweekly  1500 × 0.15 = $225
 *   every 4w  1500 × 0.17 = $255
 *   one-time  1500 × 0.20 = $300
 *   deep      1500 × 0.28 = $420
 *   unfurn.   1500 × 0.39 = $585
 *
 * Bands 2 and 3 are the large-home discount and are NOT on the card. Each was
 * moved by the same factor its band 1 moved, preserving the existing taper.
 * Ordering holds in every band (weekly < biweekly < every-4 < one-time < deep)
 * and band1 > band2 > band3 for every service.
 */
export const RATE_CARD: PricingRates = {
  // $/sqft — flat, no bands, any service type
  unfurnishedPerSqft: 0.39,

  //                      band1  band2  band3      band 1 = the published rate
  furnishedMaintenance: { band1: 0.20, band2: 0.16, band3: 0.15 },
  furnishedDeep:        { band1: 0.28, band2: 0.23, band3: 0.20 },
  recurringWeekly:      { band1: 0.11, band2: 0.09, band3: 0.08 },
  recurringBiweekly:    { band1: 0.15, band2: 0.13, band3: 0.11 },
  recurring4Weeks:      { band1: 0.17, band2: 0.15, band3: 0.12 },

  // Add-on to a first recurring visit, charged on band-1 sqft only.
  deepUpgradePerSqft: 0.08,

  // Ties price to labour: estimatedHours = totalPrice / internalHourlyRate.
  // At $81/hr the wage tiers are 28% = $22.68, 30% = $24.30, 32% = $25.92,
  // all clearing the $22.24 Tampa market reference.
  internalHourlyRate: 81.0,

  // Floors. A small home still needs a real visit.
  jobMinimum: 185.0,
  deepCleanMinimum: 250.0,

  // Band edges, and the size past which a quote is advisory only.
  band1MaxSqft: 1500,
  band2MaxSqft: 3000,
  cardMaxSqft: 6000,
};

/**
 * Overlay `admin_settings` onto the rate card.
 *
 * ⚠️ DEAD ON THIS SITE TODAY. Pricing was decoupled from the Broomstack platform
 * on 2026-09-10, so nothing passes real settings — every caller uses RATE_CARD
 * directly. This is kept as the documented seam for reconnecting later, and as
 * the place that defines what each `pricing_*` settings key maps to.
 *
 * If you are reconnecting: call this with the tenant's settings instead of using
 * RATE_CARD, and make the platform's settings match RATE_CARD BEFORE shipping,
 * or the estimator's numbers change under visitors mid-session. See the header
 * of src/api/routes/pricing.ts.
 */
export function loadRatesFromSettings(settings: Record<string, string>): PricingRates {
  const n = (key: string, fallback: number): number => {
    const v = settings[key];
    if (v === undefined || v === null || v === '') return fallback;
    const parsed = parseFloat(v);
    return isNaN(parsed) ? fallback : parsed;
  };

  /** Read a band trio, defaulting each band to the card's value. */
  const bands = (base: string, card: PricingBands): PricingBands => ({
    band1: n(`pricing_${base}_per_sqft`, card.band1),
    band2: n(`pricing_${base}_band2_per_sqft`, card.band2),
    band3: n(`pricing_${base}_band3_per_sqft`, card.band3),
  });

  return {
    unfurnishedPerSqft:   n('pricing_unfurnished_per_sqft', RATE_CARD.unfurnishedPerSqft),
    furnishedMaintenance: bands('furnished_maintenance',        RATE_CARD.furnishedMaintenance),
    furnishedDeep:        bands('furnished_deep',               RATE_CARD.furnishedDeep),
    recurringWeekly:      bands('furnished_recurring_weekly',   RATE_CARD.recurringWeekly),
    recurringBiweekly:    bands('furnished_recurring_biweekly', RATE_CARD.recurringBiweekly),
    recurring4Weeks:      bands('furnished_recurring_4wks',     RATE_CARD.recurring4Weeks),
    deepUpgradePerSqft:   n('pricing_deep_clean_upgrade_per_sqft', RATE_CARD.deepUpgradePerSqft),
    internalHourlyRate:   n('pricing_internal_hourly_rate', RATE_CARD.internalHourlyRate),
    jobMinimum:           n('pricing_job_minimum', RATE_CARD.jobMinimum),
    deepCleanMinimum:     n('pricing_deep_clean_minimum', RATE_CARD.deepCleanMinimum),
    band1MaxSqft:         n('pricing_band1_max_sqft', RATE_CARD.band1MaxSqft),
    band2MaxSqft:         n('pricing_band2_max_sqft', RATE_CARD.band2MaxSqft),
    cardMaxSqft:          n('pricing_card_max_sqft', RATE_CARD.cardMaxSqft),
  };
}
